/**
 * @file Attribute resolution system for CRUD components
 * @version 1.0.0
 * @since 2025-09-01
 */

/**
 * Resolves attribute value with priority: Schema > Props > Local Attrs > Global Defaults
 * @param {string} attributeName - Name of the attribute to resolve
 * @param {object} schema - Schema object containing attribute definitions
 * @param {object} props - Component props
 * @param {object} localAttrs - Component-level attributes (e.g., $attrs)
 * @param {object} globalDefaults - Global default values
 * @returns {*} Resolved attribute value or undefined
 */
export function resolveAttribute(
  attributeName,
  schema,
  props,
  localAttrs,
  globalDefaults
) {
  // 1. Schema defined (highest priority)
  if (schema && schema[attributeName] !== undefined) {
    return schema[attributeName];
  }

  // 2. Component props
  if (props && props[attributeName] !== undefined) {
    return props[attributeName];
  }

  // 3. Local attrs (component level)
  if (localAttrs && localAttrs[attributeName] !== undefined) {
    return localAttrs[attributeName];
  }

  // 4. Global defaults (lowest priority)
  if (globalDefaults && globalDefaults[attributeName] !== undefined) {
    return globalDefaults[attributeName];
  }

  return undefined;
}

/**
 * Creates a context-bound getAttribute function for a component
 * @param {object} schema - Component schema
 * @param {object} props - Component props
 * @param {object} localAttrs - Component $attrs
 * @param {object} defaults - Default values (from defaults prop)
 * @returns {Function} getAttribute function with signature (name, defaultValue) => value
 */
export function createGetAttribute(schema, props, localAttrs, defaults) {
  return function getAttribute(name, defaultValue) {
    const value = resolveAttribute(name, schema, props, localAttrs, defaults);
    return value !== undefined ? value : defaultValue;
  };
}

// Vue provide/inject key for defaults
export const DEFAULTS_KEY = Symbol('defaults');
